import { H1, H2 } from "../../../../components/text/headings";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Checkbox,
  Fade,
  Flex,
  HStack,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useGetSupplierOverviewData } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import SupplierFiltersCatQualityYear from "../supplier-components/SupplierFiltersCatQualityYear";
import * as React from "react";
import EngagementCards from "./components/EngagementCards";
import { ArrowLeft, ChartDonut, ListBullets } from "@phosphor-icons/react";
import PillToggle from "../components/PillToggle";
import TrendVisual from "./components/TrendVisual";
import SupplierListVisual from "./components/SupplierListVisual";
import { routes } from "../../../../config";
import { useNavigate } from "react-router-dom";
import { Page } from "../../../../components/Page";
import { constructQueryParamsString, getFormattedNumValue, QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../../utils";
import { useGetSuppliersYearlyData, useChangeReportExclusion } from "../../../../hooks/query";
import { useGetSupplierEngagementReports } from "../../../../hooks/query/reports";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "../../../SupplierQuestionnaire/helpers";
import { getStatusLabel } from "../../SupplierReports/components/SuppliersDataReport/components/SupplierEngagement/components/SupplierEngagementGrid";
import { SuppliersDataModal } from "../../SupplierReports/components/SuppliersDataReport/components";
import LocalSpinner from "../../../../components/LocalSpinner";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";

const ALL_YEARS = ["2021", "2022", "2023", "2024", "2025", "2026", "2027", "2028", "2029", "2030"];
const DEFAULT_YEARS = ["2021", "2022", "2023", "2024"];

const YEAR_COLORS = {
  "2021": { border: "#9F7AEA", bg: "#FAF5FF", text: "#553C9A" },
  "2022": { border: "#667EEA", bg: "#EBF4FF", text: "#3C366B" },
  "2023": { border: "#4299E1", bg: "#EBF8FF", text: "#2A4365" },
  "2024": { border: "#63AD03", bg: "#F0FFF4", text: "#276749" },
  "2025": { border: "#ED8936", bg: "#FFFAF0", text: "#7B341E" },
  "2026": { border: "#E53E3E", bg: "#FFF5F5", text: "#742A2A" },
};

const TH = (props) => (
  <Th
    py={3}
    px={4}
    fontSize="11px"
    color="gray.500"
    textTransform="uppercase"
    letterSpacing="0.04em"
    fontWeight="600"
    {...props}
  />
);

const qualityBadgeProps = (quality) => {
  if (!quality) return { bg: "gray.100", color: "gray.500" };
  const q = quality.toUpperCase();
  if (q === "VERIFIED") return { bg: "green.100", color: "green.800" };
  if (q === "MIXED") return { bg: "orange.100", color: "orange.800" };
  return { bg: "gray.100", color: "gray.500" };
};

const ghgBadgeProps = (type) => {
  if (type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.PRODUCT) return { bg: "orange.100", color: "orange.800" };
  if (type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.LOCATION) return { bg: "blue.100", color: "blue.800" };
  if (type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.REVENUE) return { bg: "green.100", color: "green.800" };
  return { bg: "gray.100", color: "gray.500" };
};

// ─── Emissions table (replaces DataQuality) ───────────────────────────────
const EmissionsTable = () => {
  const toast = useToast();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [years, setYears] = useState(DEFAULT_YEARS);

  const toggleYear = (y) =>
    setYears((prev) => (prev.includes(y) ? prev.filter((x) => x !== y) : [...prev, y].sort()));

  const queryString = useMemo(
    () => constructQueryParamsString({ years: years.length ? years : DEFAULT_YEARS, showExcluded: true }),
    [years]
  );

  const { isFetching } = useGetSuppliersYearlyData(queryString, {
    onSuccess: (data) => setRows(data || []),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const { mutate: changeExclusion } = useChangeReportExclusion({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSuccess: (_, vars) => {
      setRows((prev) =>
        prev.map((r) =>
          r.supplierId === vars.supplierId ? { ...r, excludeFromReport: vars.excludeFromReport } : r
        )
      );
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  return (
    <Box>
      <HStack spacing={2} flexWrap="wrap" mb={4}>
        {ALL_YEARS.map((y) => {
          const active = years.includes(y);
          const c = YEAR_COLORS[y] || { border: "#718096", bg: "#EDF2F7", text: "#2D3748" };
          return (
            <Tag
              key={y}
              size="sm"
              borderRadius="full"
              cursor="pointer"
              border="1px solid"
              borderColor={active ? c.border : "gray.300"}
              bg={active ? c.bg : "transparent"}
              color={active ? c.text : "gray.500"}
              fontWeight="600"
              onClick={() => !active && toggleYear(y)}
              _hover={{ bg: active ? c.bg : "gray.50" }}
              transition="all 0.15s"
            >
              <TagLabel>{y}</TagLabel>
              {active && (
                <TagCloseButton onClick={(e) => { e.stopPropagation(); toggleYear(y); }} />
              )}
            </Tag>
          );
        })}
      </HStack>

      {isFetching ? (
        <Box p={6}><LocalSpinner /></Box>
      ) : rows.length === 0 ? (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">No supplier data found.</Text>
        </Box>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <TH textAlign="left">Supplier</TH>
                {years.map((y) => (
                  <TH key={y} textAlign="right">{y} tCO₂e</TH>
                ))}
                <TH textAlign="left">Data Quality</TH>
                <TH textAlign="left">Target</TH>
                <TH textAlign="center">Exclude</TH>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, idx) => (
                <Tr
                  key={row.supplierId ?? idx}
                  borderTop={idx === 0 ? "none" : "1px solid"}
                  borderColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                >
                  <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="500">
                    {row.supplierName || "—"}
                  </Td>
                  {years.map((y) => (
                    <Td key={y} py={4} px={4} fontSize="14px" color="limeShade.700" fontWeight="600" textAlign="right">
                      {getFormattedNumValue(row.yearlyEmissionData?.[y]?.totalCo2Emissions, 2, true) || "—"}
                    </Td>
                  ))}
                  <Td py={4} px={4}>
                    <Badge
                      {...qualityBadgeProps(row.dataQuality)}
                      textTransform="capitalize"
                      fontSize="11px"
                      fontWeight="600"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                    >
                      {row.dataQuality || "—"}
                    </Badge>
                  </Td>
                  <Td py={4} px={4} fontSize="14px" color="gray.600">
                    {row.targetCommitment ? "Yes" : "No"}
                  </Td>
                  <Td py={4} px={4} textAlign="center">
                    <Checkbox
                      isChecked={!!row.excludeFromReport}
                      onChange={(e) =>
                        changeExclusion({ supplierId: row.supplierId, excludeFromReport: e.target.checked })
                      }
                      colorScheme="green"
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

// ─── Engagement status table (replaces SupplierEngagementGrid) ────────────
const GHG_YEARS = [2021, 2022, 2023, 2024];

const EngagementStatusTable = ({ categoryId }) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const openModal = (id) => { setSelectedQuestionnaireId(id); onOpen(); };
  const closeModal = () => { setSelectedQuestionnaireId(""); onClose(); };

  const { isFetching } = useGetSupplierEngagementReports(categoryId, null, {
    onSuccess: (data) => {
      const mapped = [];
      if (data && Object.keys(data).length > 0) {
        Object.keys(data).sort().forEach((supplier) => {
          Object.keys(data[supplier]).sort().forEach((username) => {
            const list = data[supplier][username];
            const decarb = list.find((q) => q.type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION);
            const row = {
              supplier,
              username,
              decarbPlan: decarb ? getStatusLabel(decarb.status, decarb.durationInMinutes) : "N/A",
              decarbId: decarb?.questionnaireId ?? null,
              decarbStatus: decarb?.status ?? null,
            };
            GHG_YEARS.forEach((year) => {
              const q = list.find((x) => x.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION && x.year === year);
              row[`status${year}`] = q ? getStatusLabel(q.status, q.durationInMinutes) : "N/A";
              row[`type${year}`] = q?.type ?? null;
              row[`id${year}`] = q?.questionnaireId ?? null;
              row[`origStatus${year}`] = q?.status ?? null;
            });
            mapped.push(row);
          });
        });
      }
      setRows(mapped);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  return (
    <Box>
      {isFetching ? (
        <Box p={6}><LocalSpinner /></Box>
      ) : rows.length === 0 ? (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">No engagement data found.</Text>
        </Box>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <TH textAlign="left">Supplier</TH>
                <TH textAlign="left">Username</TH>
                <TH textAlign="left">Decarbonisation Plan</TH>
                {GHG_YEARS.map((y) => <TH key={y} textAlign="center">{y} GHG</TH>)}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, idx) => {
                const decarbClickable =
                  row.decarbId &&
                  [QUESTIONNAIRE_STATUSES.IN_PROGRESS, QUESTIONNAIRE_STATUSES.FINALIZED].includes(row.decarbStatus);
                return (
                  <Tr
                    key={`${row.supplier}-${row.username}`}
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                  >
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="500">{row.supplier}</Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.500">{row.username}</Td>
                    <Td py={4} px={4} fontSize="14px">
                      <Text
                        color={decarbClickable ? "limeShade.700" : "gray.600"}
                        textDecoration={decarbClickable ? "underline" : "none"}
                        cursor={decarbClickable ? "pointer" : "default"}
                        onClick={decarbClickable ? () => openModal(row.decarbId) : undefined}
                      >
                        {row.decarbPlan}
                      </Text>
                    </Td>
                    {GHG_YEARS.map((y) => {
                      const isClickable =
                        row[`id${y}`] &&
                        [QUESTIONNAIRE_STATUSES.IN_PROGRESS, QUESTIONNAIRE_STATUSES.FINALIZED].includes(row[`origStatus${y}`]);
                      const bp = ghgBadgeProps(row[`type${y}`]);
                      return (
                        <Td key={y} py={4} px={4} textAlign="center">
                          <Badge
                            {...bp}
                            textTransform="none"
                            fontSize="11px"
                            fontWeight="600"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            cursor={isClickable ? "pointer" : "default"}
                            textDecoration={isClickable ? "underline" : "none"}
                            onClick={isClickable ? () => openModal(row[`id${y}`]) : undefined}
                          >
                            {row[`status${y}`]}
                          </Badge>
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
      <SuppliersDataModal questionnaireId={selectedQuestionnaireId} isOpen={isOpen} onClose={closeModal} />
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────
const SupplierEngagement = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [currentYearData, setCurrentYearData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);
  const [filters, setFilters] = useState();

  const { data: overviewData, isFetching } = useGetSupplierOverviewData(
    [filters?.year, filters?.year - 1],
    filters?.categories,
    filters?.dataQuality,
    null,
    {
      onError: (error) => {
        if (error?.response?.status !== 404) {
          toast({ title: "Error fetching supplier data", description: error.message, status: "error", duration: 5000, isClosable: true });
        }
      },
      cacheTime: 60,
    }
  );

  useEffect(() => {
    if (overviewData) {
      setCurrentYearData(overviewData[filters?.year]);
      setPreviousYearData(overviewData[filters?.year - 1]);
    }
  }, [overviewData, filters?.year]);

  return (
    <Page>
      <Flex direction="column" gap={2} mx="10%">
        <Flex onClick={() => navigate(routes.reportingPortalSupplierInsights)} align="center" gap={2} cursor="pointer">
          <ArrowLeft size={22} color="#85C22A" />
          <Text>Value Chain</Text>
        </Flex>

        <H1 fontSize="heading.xl">Supplier Engagement</H1>

        <SupplierFiltersCatQualityYear
          filters={filters}
          setFilters={setFilters}
          showFilters={["year", "quality", "category"]}
        />

        <EngagementCards
          currentYearData={currentYearData}
          previousYearData={previousYearData}
          isLoading={isFetching}
        />

        <EmissionsSection filters={filters} setFilters={setFilters} />
        <TrendsSection filters={filters} setFilters={setFilters} />
      </Flex>
    </Page>
  );
};

const EmissionsSection = ({ filters, setFilters }) => {
  const [view, setView] = useState("table");
  return (
    <Box mt={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <H2 fontSize="22px" fontWeight="700" color="darkTeal">Supplier Emissions</H2>
        <PillToggle
          options={[
            { label: "Table", value: "table", icon: <ListBullets size={14} /> },
            { label: "Visual", value: "visual", icon: <ChartDonut size={14} /> },
          ]}
          selected={view}
          setSelected={setView}
        />
      </Flex>
      <Fade in={view === "table"} unmountOnExit>
        <EmissionsTable />
      </Fade>
      <Fade in={view === "visual"} unmountOnExit>
        <SupplierListVisual filters={filters} setFilters={setFilters} />
      </Fade>
    </Box>
  );
};

const TrendsSection = ({ filters, setFilters }) => {
  const [view, setView] = useState("table");
  return (
    <Box my={2}>
      <Accordion allowToggle>
        <AccordionItem>
          <AccordionButton>
            <HStack flex="1" justify="space-between" align="center">
              <H2 fontSize="22px" fontWeight="700" color="darkTeal">Engagement Status</H2>
              <Flex align="center" gap={3} onClick={(e) => e.stopPropagation()}>
                <PillToggle
                  options={[
                    { label: "Table", value: "table", icon: <ListBullets size={14} /> },
                    { label: "Visual", value: "visual", icon: <ChartDonut size={14} /> },
                  ]}
                  selected={view}
                  setSelected={setView}
                />
              </Flex>
            </HStack>
            <AccordionIcon mr={0} ml={4} />
          </AccordionButton>
          <AccordionPanel mt={4}>
            <Fade in={view === "table"} unmountOnExit>
              <EngagementStatusTable categoryId={null} />
            </Fade>
            <Fade in={view === "visual"} unmountOnExit>
              <TrendVisual setFilters={setFilters} filters={filters} />
            </Fade>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

export default SupplierEngagement;
